import {
  OnSuccessPromise,
  StripeCanMakePayment,
  StripePaymentRequest,
  createStripePaymentRequest,
  getPaymentRequestTotal,
  logApplePayError,
  startStripeSession,
} from 'utils/applePay';
import {
  UseProcessorApplePayParams,
  UseProcessorApplePayResults,
} from 'hooks/apple-pay/useApplePay';
import { useCallback, useEffect, useState } from 'react';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useMountEffect } from '@tock/hooks';

export const useApplePayForStripe = (
  params: UseProcessorApplePayParams
): UseProcessorApplePayResults => {
  const [paymentRequest, setPaymentRequest] = useState<StripePaymentRequest>();

  const isFeatureEnabledForStripe = useFeatureFlag('ENABLE_APPLE_PAY_FOR_STRIPE');

  const canMakePaymentRequests =
    params.isSupportedTockEnvironment && isFeatureEnabledForStripe && !!window.stripeV3;

  useMountEffect(() => {
    if (
      !canMakePaymentRequests ||
      !params.amountCents ||
      !params.countryCode ||
      !params.currencyCode
    ) {
      return;
    }

    try {
      const request = createStripePaymentRequest(
        params.amountCents,
        params.countryCode,
        params.currencyCode
      );

      request
        .canMakePayment()
        .then((canMakePayment: StripeCanMakePayment | null) => {
          params.setIsSupportedDevice(!!canMakePayment?.applePay);
          params.setHasCardInAppleWallet(!!canMakePayment?.applePay);
        })
        .catch(() => {
          logApplePayError('STRIPE', 'init', 'Failed to check if device can make payments');
          params.setFailed(true);
        });

      setPaymentRequest(request);
    } catch (_error) {
      logApplePayError('STRIPE', 'init', 'Failed to create Stripe payment request');
      params.setFailed(true);
    }
  });

  const startSession = useCallback(
    (onSuccess: OnSuccessPromise) => {
      if (!paymentRequest) {
        return Promise.reject('Apple Pay failed to load');
      }

      return startStripeSession(paymentRequest, onSuccess);
    },
    [paymentRequest]
  );

  /**
   * Watch for changes in the total (ex: discount codes) so that we show the most
   * up to date total in the Apple Pay UI when processing the transaction
   */
  useEffect(() => {
    if (paymentRequest && params.amountCents && params.currencyCode) {
      paymentRequest.update({
        total: getPaymentRequestTotal(params.amountCents, params.currencyCode),
      });
    }
  }, [params.amountCents, params.currencyCode, paymentRequest]);

  return {
    isEnabled: canMakePaymentRequests,
    isLoading: canMakePaymentRequests && !paymentRequest,
    startSession: startSession,
  };
};
